import React, { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { User, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { BASE_URL } from '@/config/api';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle
} from '@/components/ui/card';
import {
  Form, FormControl, FormField, FormItem, FormLabel
} from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ArrowLeft, Calendar as CalendarIcon, CreditCard, Plus, Trash, AlertCircle, Info
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DateRange as RDPDateRange } from 'react-day-picker';
import moment from 'moment';

interface EventItem {
  id: string;
  date: string;
  fullDay: boolean;
  category: string;
  categoryId: string;
  countStudents:number;
  categoryName: string;
  categoryColor: string;
  eventType: any;
  title: string;
  duration: number;
  price: number;
  event_extra_expenses?: number;
  description: string;
  selected: boolean;
}

interface TaxItem {
  id: string;
  name: string;
  rate: number;
  selected: boolean;
  amount?: number;
}

interface ExpenseItem {
  name: string;
  amount: number;
  id?: string;
}

interface AdditionalFee {
  description: string;
  amount: number;
  id?: string;
}

type DateRange = Partial<RDPDateRange>;

const additionalFeeSchema = z.object({
  description: z.string().min(1, { message: "Description is required" }),
  amount: z.number().min(0.01, { message: "Amount must be greater than 0" })
});

const invoiceSchema = z.object({
  invoiceNumber: z.string().optional(),
  invoiceDate: z.date(),
  dueDate: z.date(),
  discountType: z.enum(['none', 'percentage', 'amount']).default('none'),
  discountValue: z.number().min(0).default(0),
  note: z.string().optional(),
});

const InvoiceEdit = () => {
  const { t, locale } = useLanguage();
  const { user, token } = useAuth();
  // Type de taxe propre à la facture : 'O' = taxe incluse (inversée), 'N' = normal
  const [invoiceCreationType, setInvoiceCreationType] = useState<string | null>(null);
  const taxeType = invoiceCreationType
    ? (String(invoiceCreationType).toUpperCase() === 'O' ? 1 : 0)
    : (Number(user?.teacher?.taxe_type) === 1 ? 1 : 0);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [pendingEvents, setPendingEvents] = useState<EventItem[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<EventItem[]>([]);
  const [taxes, setTaxes] = useState<TaxItem[]>([]);
  const [discountType, setDiscountType] = useState<'none' | 'percentage' | 'amount'>('none');
  const [discountValue, setDiscountValue] = useState<number>(0);
  const [categories, setCategories] = useState<any[]>([]);
  const [isUpdatingInvoice, setIsUpdatingInvoice] = useState<boolean>(false);
  const [additionalFees, setAdditionalFees] = useState<AdditionalFee[]>([]);
  const [studentId, setStudentId] = useState<string>('');
  const [studentName, setStudentName] = useState<string>('');
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [billingPeriodRange, setBillingPeriodRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [billingCalendarMonth, setBillingCalendarMonth] = useState<Date | undefined>();
  const [invoiceDateMonth, setInvoiceDateMonth] = useState<Date>(new Date());
  const [dueDateMonth, setDueDateMonth] = useState<Date>(new Date());

  const form = useForm<z.infer<typeof invoiceSchema>>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      invoiceNumber: '',
      invoiceDate: new Date(),
      dueDate: new Date(),
      discountType: 'none',
      discountValue: 0,
      note: '',
    },
  });

  // Fetch invoice data
  useEffect(() => {
    const fetchInvoiceData = async () => {
      try {
        setInitialLoading(true);
        const response = await fetch(`${BASE_URL}/api/invoice/${id}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch invoice');
        }
        
        const data = await response.json();
        const invoice = data.invoice;
        setInvoiceCreationType(invoice.invoice_creation_type ?? null);
        
        // Set student ID and name
        setStudentId(invoice.client_id);
        setStudentName(`${invoice.student.name}`);
        
        // Initialize form values
        form.reset({
          invoiceNumber: invoice.number,
          invoiceDate: moment(invoice.date).toDate(),
          dueDate: moment(invoice.dueDate).toDate(),
          note: invoice.note || '',
        });
        
        // Set discount
        if (invoice.discount_percent_1 > 0) {
          setDiscountType('percentage');
          setDiscountValue(invoice.discount_percent_1);
        } else if (invoice.amount_discount_1 > 0) {
          setDiscountType('amount');
          setDiscountValue(invoice.amount_discount_1);
        }
        
        // Set items/events
        const formattedEvents = invoice.items.map((item: any) => ({
          id: item.id || `temp-${Date.now()}-${Math.random()}`,
          date: item.date,
          fullDay: item.fullDay || false,
          category: item.category || 'Lesson',
          categoryId: item.categoryId || '',
          countStudents: item.countStudents || 0,
          categoryName: item.categoryName || "",
          eventType: item.eventType || 10,
          categoryColor: item.categoryColor || '#3498db',
          duration: item.quantity || 60,
          price: item.total || 0,
          event_extra_expenses: item.event_extra_expenses || 0,
          description: item.description || '',
          title: item.description || 'Lesson',
          selected: true,
        }));
        
        setPendingEvents(formattedEvents);
        setFilteredEvents(formattedEvents);
        
        // Set taxes
        if (invoice.taxes && invoice.taxes.length > 0) {
          const formattedTaxes = invoice.taxes.map((tax: any) => ({
            id: tax.id || `temp-${Date.now()}-${Math.random()}`,
            name: tax.name,
            rate: tax.rate,
            selected: true,
            amount: tax.amount
          }));
          setTaxes(formattedTaxes);
        } else {
          // Fetch taxes if invoice doesn't have any
          fetchTaxes();
        }
        
        // Set additional fees
        if (invoice.expenses && invoice.expenses.length > 0) {
          const formattedFees = invoice.expenses.map((expense: any) => ({
            description: expense.name,
            amount: expense.amount,
            id: expense.id || `temp-${Date.now()}-${Math.random()}`
          }));
          setAdditionalFees(formattedFees);
        }

        // Set billing period
        if (invoice.period_starts && invoice.period_ends) {
          const fromDate = moment(invoice.period_starts).toDate();
          setBillingPeriodRange({
            from: fromDate,
            to: moment(invoice.period_ends).toDate(),
          });
          setBillingCalendarMonth(fromDate);
        } else if (formattedEvents.length > 0) {
          const mostRecentEvent = formattedEvents.reduce((latest, event) =>
            moment(event.date).isAfter(moment(latest.date)) ? event : latest
          );
          const mostRecentDate = moment(mostRecentEvent.date);
          const firstDay = mostRecentDate.startOf('month').toDate();
          const lastDay = mostRecentDate.endOf('month').toDate();
          setBillingPeriodRange({ from: firstDay, to: lastDay });
          setBillingCalendarMonth(firstDay);
        }

        // Extract categories from events
        if (formattedEvents.length > 0) {
          const uniqueCategories = Array.from(
            new Set(formattedEvents.map(event => event.categoryId))
          ).map(categoryId => {
            const event = formattedEvents.find(event => event.categoryId === categoryId);
            return {
              id: categoryId,
              name: event?.category || '',
              color: event?.categoryColor || '#3498db'
            };
          });
          setCategories(uniqueCategories);
        }
        
      } catch (error) {
        console.error('Error fetching invoice data:', error);
        toast({
          title: t('invoiceDetail.errorFetchingInvoice'),
          description: String(error),
          variant: 'destructive',
        });
      } finally {
        setInitialLoading(false);
      }
    };
    
    if (id && token) {
      fetchInvoiceData();
    }
  }, [id, token, t, toast, form]);

  const fetchTaxes = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/billing_taxes`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch taxes');
      }

      const data = await response.json();
      const formattedTaxes = (data.taxes || []).map((tax: any) => ({
        id: tax.id,
        name: tax.name,
        rate: tax.rate,
        selected: tax.selected || tax.id === '1',
        amount: 0,
      }));
      setTaxes(formattedTaxes);
    } catch (error) {
      console.error('Error fetching taxes:', error);
    }
  };

  useEffect(() => {
    const subtotal = getSubtotal();
    const discount = getDiscountAmount();
    const taxableAmount = subtotal - discount;

    setTaxes((prevTaxes) =>
      prevTaxes.map((tax) => ({
        ...tax,
        amount: tax.selected
          ? (taxeType === 1
              ? taxableAmount - taxableAmount / (1 + tax.rate / 100)
              : (taxableAmount * tax.rate) / 100)
          : 0,
      }))
    );
  }, [filteredEvents, taxes.map((tax) => tax.selected), discountType, discountValue]);

  const addAdditionalFee = () => {
    setAdditionalFees([
      ...additionalFees,
      { description: "", amount: 0 }
    ]);
  };

  const removeAdditionalFee = (index: number) => {
    setAdditionalFees(additionalFees.filter((_, i) => i !== index));
  };

  const handleAdditionalFeeChange = (index: number, field: keyof AdditionalFee, value: string | number) => {
    const updatedFees = [...additionalFees];
    if (field === 'description') {
      updatedFees[index].description = value as string;
    } else if (field === 'amount') {
      updatedFees[index].amount = value as number;
    }
    setAdditionalFees(updatedFees);
  };

  const getAdditionalFeesTotal = () => {
    return additionalFees.reduce((sum, fee) => sum + (Number(fee.amount) || 0), 0);
  };

  const translateStr = (key: string): string => {
    const translated = t(key);
    return Array.isArray(translated) ? translated.join(' ') : translated;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: user.currency_code || '$'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
      const date = moment(dateString);
      return date.format(locale === 'fr' ? 'DD/MM/YYYY' : 'MM/DD/YYYY');
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) {
      return `${mins}min`;
    } else if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${mins}min`;
    }
  };

  const removeAccents = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  const invoiceDate = form.watch("invoiceDate");
  const invoiceNumber = form.watch("invoiceNumber");

  useEffect(() => {
    if (studentName && invoiceDate) {
      if (invoiceNumber && invoiceNumber.startsWith('INV-')) {
      let month = invoiceDate.toLocaleString('default', { month: 'short' });
      month = removeAccents(month).replace('.', '').toLowerCase();

      const invoiceNameInit = studentName.trim().toLowerCase().replace(/\s+/g, '-');
      const year = moment(invoiceDate).format('YYYY');
      const newInvoiceNumberInit = `${invoiceNameInit}-${month}-${year}`;
      const newInvoiceNumberGet = newInvoiceNumberInit.toLowerCase().replace(/\s+/g, '-');
      const newInvoiceNumber = newInvoiceNumberGet.toLowerCase().replace('--', '-');
      form.setValue('invoiceNumber', newInvoiceNumber);
    }}
  }, [studentName, invoiceDate, form]);


  const handleToggleEvent = (eventId: string) => {
    setPendingEvents((prev) =>
      prev.map((event) =>
        event.id === eventId ? { ...event, selected: !event.selected } : event
      )
    );
    setFilteredEvents((prev) =>
      prev.map((event) =>
        event.id === eventId ? { ...event, selected: !event.selected } : event
      )
    );
  };

  const handleToggleTax = (taxId: string) => {
    setTaxes((prev) =>
      prev.map((tax) =>
        tax.id === taxId ? { ...tax, selected: !tax.selected } : tax
      )
    );
  };

  const getSubtotalWithExtra = () => {
    return filteredEvents
      .filter((event) => event.selected)
      .reduce((sum, event) => sum + event.price + event.event_extra_expenses, 0);
  };

  const getSubtotal = () => {
    return filteredEvents
      .filter((event) => event.selected)
      .reduce((sum, event) => sum + event.price, 0);
  };

  const getExtraCharges = () => {
    return filteredEvents
      .filter((event) => event.selected)
      .reduce((sum, event) => sum + event.event_extra_expenses, 0);
  };

  const getDiscountAmount = () => {
    const subtotal = getSubtotal();

    if (discountType === 'none' || discountValue === 0) {
      return 0;
    } else if (discountType === 'percentage') {
      return (subtotal * discountValue) / 100;
    } else {
      return discountValue;
    }
  };

  const getTaxAmount = () => {
    const subtotal = getSubtotal();
    const discount = getDiscountAmount();
    const taxableAmount = subtotal - discount;

    return taxes
      .filter((tax) => tax.selected)
      .reduce((sum, tax) => sum + (taxeType === 1
        ? taxableAmount - taxableAmount / (1 + tax.rate / 100)
        : (taxableAmount * tax.rate) / 100), 0);
  };

  const getTotal = () => {
    const subtotal = getSubtotal();
    const discount = getDiscountAmount();
    const tax = getTaxAmount();
    const fees = getAdditionalFeesTotal();
      const extraCharges = getExtraCharges();

    // taxeType === 1 : la taxe est déjà incluse dans les prix (TTC), on ne l'ajoute pas
    return subtotal - discount + (taxeType === 1 ? 0 : tax) + fees + extraCharges;
  };

  const onSubmit = async (values: z.infer<typeof invoiceSchema>) => {
    const selectedEvents = filteredEvents.filter((event) => event.selected);

    if (selectedEvents.length === 0) {
      toast({
        variant: "destructive",
        title: translateStr('invoices.noEventsSelected'),
        description: translateStr('invoices.pleaseSelectEvents'),
      });
      return;
    }

    let finalBillingPeriodRange = { ...billingPeriodRange };

    // If there's a start date but no end date, set the end date to the end of the month.
    if (finalBillingPeriodRange.from && !finalBillingPeriodRange.to) {
      finalBillingPeriodRange.to = moment(finalBillingPeriodRange.from).endOf('month').toDate();
    }

    const adjustedBillingPeriod = {
      from: finalBillingPeriodRange.from ? moment(finalBillingPeriodRange.from).format('YYYY-MM-DD') : undefined,
      to: finalBillingPeriodRange.to ? moment(finalBillingPeriodRange.to).format('YYYY-MM-DD') : undefined,
    };

    const invoiceData = {
      ...values,
      invoiceDate: moment(values.invoiceDate).format('YYYY-MM-DD'),
      dueDate: moment(values.dueDate).format('YYYY-MM-DD'),
      billingPeriod: adjustedBillingPeriod,
      events: selectedEvents,
      taxes: taxes.filter((tax) => tax.selected),
      subtotal: getSubtotal(),
      discount: {
        type: discountType,
        value: discountValue,
        amount: getDiscountAmount(),
      },
      taxAmount: getTaxAmount(),
      additionalFeesTotal: getAdditionalFeesTotal(),
      additionalFees: additionalFees,
      total: getTotal(),
      studentId,
      invoiceId: id // Pass the invoice ID for update
    };

    setIsUpdatingInvoice(true);
    console.log(invoiceData);

    try {
      const response = await fetch(`${BASE_URL}/api/invoice/update`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(invoiceData),
      });

      if (!response.ok) {
        throw new Error('Failed to update invoice');
      }

      const data = await response.json();

      toast({
        title: translateStr('invoices.invoiceUpdated'),
        description: translateStr('invoices.invoiceUpdatedDescription'),
      });

      navigate('/invoices/' + id);
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast({
        variant: "destructive",
        title: translateStr('invoices.invoiceUpdateFailed'),
        description: translateStr('invoices.invoiceUpdateFailedDescription'),
      });
    } finally {
      setIsUpdatingInvoice(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 container py-8 mt-16 flex items-center justify-center">
          <p className="text-muted-foreground">{t('common.loading')}...</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1 container py-8 mt-16">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate(`/invoices/${id}`)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {translateStr('common.back')}
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <CardTitle>
                    <div className="flex items-center gap-3">
                      <User className="h-6 w-6" />
                      <span>{studentName}</span>
                    </div>
                  </CardTitle>
                </div>
                <CardDescription>
                  {translateStr('invoices.editInvoice')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateStr('invoices.items')}</CardTitle>
                <CardDescription>
                  {translateStr('invoices.selectEventsToInvoice')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {filteredEvents.length > 0 ? (
                  <div className="rounded-md border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]"></TableHead>
                          <TableHead>{translateStr('invoices.date')}</TableHead>
                          {/*<TableHead>{translateStr('invoices.category')}</TableHead>*/}
                          <TableHead>{translateStr('invoices.description')}</TableHead>
                          <TableHead>{translateStr('invoices.duration')}</TableHead>  
                          <TableHead className="text-right">{translateStr('invoices.amount')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredEvents.map((event) => (
                          <TableRow key={event.id}>
                            <TableCell>
                              <Checkbox
                                checked={event.selected}
                                onCheckedChange={() => handleToggleEvent(event.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                {formatDate(event.date)}
                              </div>
                            </TableCell>
                            {/*<TableCell>
                              <Badge
                                variant="outline"
                                style={{
                                  backgroundColor: `${event.categoryColor}20`,
                                  color: event.categoryColor,
                                  borderColor: event.categoryColor
                                }}
                              >
                                {event.category}
                              </Badge>
                            </TableCell>*/}
                            <TableCell className="max-w-[270px] truncate">
                              {event.title === "Lesson" || event.title === "Event" ? t('agenda.'+event.title) : event.title}  
                              {event.eventType === 10 ? ' (' + event.categoryName + ')' : ''}
                              {event.countStudents > 1 ? ' ' + t('common.with') : ''} {event.countStudents > 1 ? event.countStudents : ''} {event.countStudents > 1 ? t('students.students') : '(' + t('agenda.privateLesson') + ')'}
                            </TableCell>
                            <TableCell>{event.eventType === 10 ? formatDuration(event.duration) : '-'}</TableCell>  
                            <TableCell className="text-right font-medium">
                              {formatCurrency(event.price)}
                               {event.event_extra_expenses > 0 && (
                                <>
                                <br/>
                                  <span className="text-xs">
                                    + {formatCurrency(event.event_extra_expenses)} {translateStr('invoices.extraCharges')}
                                  </span>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {translateStr('invoices.noItems')}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-5">
            <Card>
              <CardHeader>
                <div className="flex items-center">
                  <CardTitle>
                    {translateStr('invoices.parameters')}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form className="space-y-4 ">
                    <FormField
                      control={form.control}
                      name="invoiceNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{translateStr('invoices.invoiceNumber')}</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <div>
                      <Label htmlFor="billing-period-range">
                        <div className="flex items-center">
                          {translateStr('invoices.issueDate')}
                          <TooltipProvider>
                            <Tooltip delayDuration={100}>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 text-muted-foreground ml-2 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t('invoices.billingPeriodDateTooltip', "The billing period will be used in the annual billing report")}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            id="billing-period-range"
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal mt-2",
                              !billingPeriodRange.from && "text-muted-foreground"
                            )}
                          >
                            {billingPeriodRange.from ? (
                              billingPeriodRange.to ? (
                                <>
                                  {moment(billingPeriodRange.from).format(locale === 'fr' ? 'DD/MM/YYYY' : 'MM/DD/YYYY')} -{" "}
                                  {moment(billingPeriodRange.to).format(locale === 'fr' ? 'DD/MM/YYYY' : 'MM/DD/YYYY')}
                                </>
                              ) : (
                                moment(billingPeriodRange.from).format("ll")
                              )
                            ) : (
                              <span>{translateStr('invoices.pickDateRange')}</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                            initialFocus
                            mode="range"
                            month={billingCalendarMonth}
                            onMonthChange={setBillingCalendarMonth}
                            selected={billingPeriodRange as RDPDateRange}
                            onSelect={(range) => {
                                const newRange = range || { from: undefined, to: undefined };
                                setBillingPeriodRange(newRange);
                                if (newRange.from) {
                                    setBillingCalendarMonth(newRange.from);
                                }
                            }}
                            numberOfMonths={1}
                            className={cn("p-3 pointer-events-auto")}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="invoiceDate"
                        render={({ field }) => {
                          const [invoiceDateMonth, setInvoiceDateMonth] = useState<Date>(
                            field.value ? moment(field.value).toDate() : new Date()
                          );

                          useEffect(() => {
                            if (field.value) {
                              setInvoiceDateMonth(moment(field.value).toDate());
                            }
                          }, [field.value]);

                          return (
                            <FormItem>
                              <FormLabel>
                                  {translateStr('invoices.invoiceDate')}
                              </FormLabel>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      variant="outline"
                                      className={cn(
                                        "w-full pl-3 text-left font-normal",
                                        !field.value && "text-muted-foreground"
                                      )}
                                    >
                                      {field.value ? (
                                        moment(field.value).format(locale === 'fr' ? 'DD/MM/YYYY' : 'MM/DD/YYYY')
                                      ) : (
                                        <span>{translateStr('common.selectDate')}</span>
                                      )}
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <Calendar
                                    mode="single"
                                    selected={field.value ? moment(field.value).toDate() : undefined}
                                    onSelect={(date) => {
                                      const formattedDate = moment(date).format('YYYY-MM-DD');
                                      field.onChange(moment(formattedDate).toDate());
                                      setInvoiceDateMonth(moment(date).toDate());
                                    }}
                                    month={invoiceDateMonth}
                                    onMonthChange={setInvoiceDateMonth}
                                    initialFocus
                                    className={cn("p-3 pointer-events-auto")}
                                  />
                                </PopoverContent>
                              </Popover>
                            </FormItem>
                          );
                        }}
                      />


                      <FormField
                        control={form.control}
                        name="dueDate"
                        render={({ field }) => {
                          const [dueDateMonth, setDueDateMonth] = useState<Date>(
                            field.value ? moment(field.value).toDate() : new Date()
                          );

                          useEffect(() => {
                            if (field.value) {
                              setDueDateMonth(moment(field.value).toDate());
                            }
                          }, [field.value]);

                          return (
                            <FormItem>
                              <FormLabel>{translateStr('invoices.dueDate')}</FormLabel>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      variant="outline"
                                      className={cn(
                                        "w-full pl-3 text-left font-normal",
                                        !field.value && "text-muted-foreground"
                                      )}
                                    >
                                      {field.value ? (
                                        moment(field.value).format(locale === 'fr' ? 'DD/MM/YYYY' : 'MM/DD/YYYY')
                                      ) : (
                                        <span>{translateStr('common.selectDate')}</span>
                                      )}
                                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <Calendar
                                    mode="single"
                                    selected={field.value ? moment(field.value).toDate() : undefined}
                                    onSelect={(date) => {
                                      const formattedDate = moment(date).format('YYYY-MM-DD');
                                      field.onChange(moment(formattedDate).toDate());
                                      setDueDateMonth(moment(date).toDate());
                                    }}
                                    month={dueDateMonth}
                                    onMonthChange={setDueDateMonth}
                                    initialFocus
                                    className={cn("p-3 pointer-events-auto")}
                                    disabled={(date) => {
                                      const invoiceDate = moment(form.getValues().invoiceDate);
                                      return moment(date).isBefore(invoiceDate, 'day');
                                    }}
                                  />
                                </PopoverContent>
                              </Popover>
                            </FormItem>
                          );
                        }}
                      />

                    </div>

                    <FormField
                      control={form.control}
                      name="note"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{translateStr('invoices.notes')}</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder={translateStr('invoices.notesPlaceholder')}
                              className="resize-none h-20"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </form>
                </Form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateStr('invoices.taxes')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {taxes.length === 0 ? (
                    <p className="text-sm font-medium">No taxes</p>
                  ) : (
                    taxes.map((tax) => (
                      <div key={tax.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`tax-${tax.id}`}
                          checked={tax.selected}
                          onCheckedChange={() => handleToggleTax(tax.id)}
                        />
                        <label
                          htmlFor={`tax-${tax.id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {tax.name} ({tax.rate}%)
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateStr('invoices.discount')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Select
                      value={discountType}
                      onValueChange={(value: 'none' | 'percentage' | 'amount') => {
                        setDiscountType(value);
                        if (value === 'none') {
                          setDiscountValue(0);
                        }
                      }}
                    >
                      <SelectTrigger id="discount-type">
                        <SelectValue placeholder={translateStr('invoices.selectDiscountType')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{translateStr('invoices.noDiscount')}</SelectItem>
                        <SelectItem value="percentage">{translateStr('invoices.percentageDiscount')}</SelectItem>
                        <SelectItem value="amount">{translateStr('invoices.amountDiscount')}</SelectItem>
                      </SelectContent>
                    </Select>

                    {discountType !== 'none' && (
                      <div className="flex">
                        <Input
                          type="number"
                          min="0"
                          value={discountValue}
                          onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                          className="rounded-r-none"
                        />
                        <div className="flex items-center justify-center px-3 py-2 border border-l-0 rounded-r-md bg-muted">
                          {discountType === 'percentage' ? '%' : '€'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateStr('invoices.additionalFees')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {additionalFees.map((fee, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={fee.description}
                        onChange={(e) => handleAdditionalFeeChange(index, 'description', e.target.value)}
                        placeholder={translateStr('invoices.feeDescriptionPlaceholder')}
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        inputMode="numeric"
                        value={fee.amount}
                        onChange={(e) => handleAdditionalFeeChange(index, 'amount', parseFloat(e.target.value) || 0)}
                        onFocus={(e) => {
                          e.target.select();
                        }}
                        placeholder={translateStr('invoices.amount')}
                        className="w-24"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeAdditionalFee(index)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addAdditionalFee}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {translateStr('invoices.addFee')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{translateStr('invoices.summary')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm">{taxeType === 1 ? translateStr('invoices.subtotalHT') : translateStr('invoices.subtotal')}:</span>
                    <span className="text-sm font-medium">{formatCurrency(taxeType === 1 ? getSubtotalWithExtra() - getTaxAmount() : getSubtotalWithExtra())}</span>
                  </div>

                  {discountType !== 'none' && discountValue > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span className="text-sm">{translateStr('invoices.discount')}:</span>
                      <span className="text-sm font-medium">-{formatCurrency(getDiscountAmount())}</span>
                    </div>
                  )}

                  {taxes.some(tax => tax.selected) && (
                    <div className="flex justify-between">
                      <span className="text-sm">{taxeType === 1 ? translateStr('invoices.taxIncluded') : translateStr('invoices.tax')}:</span>
                      <span className="text-sm font-medium">{formatCurrency(getTaxAmount())}</span>
                    </div>
                  )}

                  {additionalFees.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-sm">{translateStr('invoices.additionalFees')}:</span>
                      <span className="text-sm font-medium">{formatCurrency(getAdditionalFeesTotal())}</span>
                    </div>
                  )}

                  <Separator />

                  <div className="flex justify-between pt-2">
                    <span className="text-base font-bold">{taxeType === 1 ? translateStr('invoices.totalTTC') : translateStr('invoices.total')}:</span>
                    <span className="text-base font-bold">{formatCurrency(getTotal())}</span>
                  </div>

                  <div className="mt-6">
                    <Button 
                      className="w-full" 
                      onClick={form.handleSubmit(onSubmit)}
                      disabled={isUpdatingInvoice}
                    >
                      {isUpdatingInvoice ? (
                        <>
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          {translateStr('common.loading')}
                        </>
                      ) : (
                        <>
                          <FileText className="h-4 w-4 mr-2" />
                          {translateStr('invoices.buttons.update')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default InvoiceEdit;